import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../Modal/Modal';
import api from '../../../utils/api';
import './ImageCropper.css';

/**
 * The framings a short is allowed to go out in.
 *
 * `ratio` drives the crop box on screen; `width`/`height` are what the applied
 * image is actually rendered at, so the result does not depend on how big the
 * editor's browser window happened to be. 16:9 is first because it is what the
 * feed card is laid out for.
 */
export const CROP_PRESETS = [
  { label: '16:9', ratio: 16 / 9, width: 1280, height: 720 },
  { label: '4:3', ratio: 4 / 3, width: 1200, height: 900 },
  { label: '1:1', ratio: 1, width: 1080, height: 1080 },
];

// Smaller than this and the handles overlap each other
const MIN_BOX_PX = 40;

/**
 * Pull a remote image in as a blob URL.
 *
 * Drawing a cross-origin image on a canvas taints it, and `toBlob()` on a
 * tainted canvas throws SecurityError - publisher images and the Inshorts CDN
 * both send no CORS headers, so this is not avoidable per host. Fetching the
 * bytes through our own backend and handing the canvas an object URL instead
 * makes the draw same-origin, which keeps the canvas readable.
 */
const loadAsObjectUrl = async (src) => {
  const blob = await api.get('/news/image-proxy', {
    params: { url: src },
    responseType: 'blob',
  });
  return URL.createObjectURL(blob);
};

/**
 * Fit a crop box of the given ratio inside the displayed image, centred.
 * Used both on open and whenever the editor switches preset.
 */
const centredBoxFor = (ratio, frame) => {
  if (!frame?.width || !frame?.height) return null;

  let width = frame.width;
  let height = width / ratio;

  if (height > frame.height) {
    height = frame.height;
    width = height * ratio;
  }

  return {
    x: (frame.width - width) / 2,
    y: (frame.height - height) / 2,
    width,
    height,
  };
};

const clampBox = (box, frame, ratio) => {
  let { x, y, width, height } = box;

  // Never wider or taller than the image itself, ratio intact
  if (width > frame.width) {
    width = frame.width;
    height = width / ratio;
  }
  if (height > frame.height) {
    height = frame.height;
    width = height * ratio;
  }

  x = Math.min(Math.max(0, x), frame.width - width);
  y = Math.min(Math.max(0, y), frame.height - height);

  return { x, y, width, height };
};

/**
 * Crop an image to one of the allowed framings and hand back the result as a
 * JPEG blob.
 *
 * `src` is the address of an image already on the CDN - both the file picker
 * and the paste-a-URL field upload before the editor can crop, so there is no
 * local-file case to handle here.
 */
const ImageCropper = ({ isOpen, src, onCancel, onApply }) => {
  const imageRef = useRef(null);
  // Drag state lives in a ref: a pointer move must not re-render per frame
  const dragRef = useRef(null);

  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [presetIndex, setPresetIndex] = useState(0);
  // Size of the <img> as laid out, which is the coordinate space the box uses
  const [frame, setFrame] = useState(null);
  const [box, setBox] = useState(null);

  const preset = CROP_PRESETS[presetIndex];

  // Pull the bytes in whenever the source changes
  useEffect(() => {
    if (!isOpen || !src) return;

    let cancelled = false;
    let created = null;

    const load = async () => {
      setLoadError(null);
      setFrame(null);
      setBox(null);
      setLoading(true);

      try {
        created = await loadAsObjectUrl(src);
        if (cancelled) {
          URL.revokeObjectURL(created);
          return;
        }
        setObjectUrl(created);
      } catch (err) {
        console.error('Could not load image for cropping:', err);
        setLoadError('Could not load this image for cropping');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [isOpen, src]);

  // Seed the box once the image has been laid out, and re-seed on preset change
  const resetBox = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    const nextFrame = { width: img.clientWidth, height: img.clientHeight };
    if (!nextFrame.width || !nextFrame.height) return;

    setFrame(nextFrame);
    setBox(centredBoxFor(preset.ratio, nextFrame));
  }, [preset.ratio]);

  useEffect(() => {
    if (objectUrl) resetBox();
  }, [objectUrl, presetIndex, resetBox]);

  // The modal animates in and the panel is resizable, so the laid-out size can
  // change under us after the first measure
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => resetBox();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, resetBox]);

  const onPointerDown = (mode) => (e) => {
    if (!box || !frame) return;
    e.preventDefault();
    e.stopPropagation();

    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...box },
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || !frame) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const { origin, mode } = drag;

    if (mode === 'move') {
      setBox(clampBox({ ...origin, x: origin.x + dx, y: origin.y + dy }, frame, preset.ratio));
      return;
    }

    // Resize from a corner: the dragged corner follows the pointer, the
    // opposite one stays put, and width drives height so the ratio holds
    const signX = mode === 'ne' || mode === 'se' ? 1 : -1;
    const signY = mode === 'sw' || mode === 'se' ? 1 : -1;

    // Average the two axes so diagonal drags do not feel like they fight back
    const delta = (dx * signX + dy * signY) / 2;
    let width = Math.max(MIN_BOX_PX, origin.width + delta);
    let height = width / preset.ratio;

    if (height < MIN_BOX_PX) {
      height = MIN_BOX_PX;
      width = height * preset.ratio;
    }

    const right = origin.x + origin.width;
    const bottom = origin.y + origin.height;
    const x = signX === 1 ? origin.x : right - width;
    const y = signY === 1 ? origin.y : bottom - height;

    setBox(clampBox({ x, y, width, height }, frame, preset.ratio));
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const handleApply = async () => {
    const img = imageRef.current;
    if (!img || !box || !frame) return;

    setApplying(true);
    try {
      // The box is in laid-out pixels; the source has its own resolution
      const scaleX = img.naturalWidth / frame.width;
      const scaleY = img.naturalHeight / frame.height;

      const canvas = document.createElement('canvas');
      canvas.width = preset.width;
      canvas.height = preset.height;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      // Flatten onto white: the output is JPEG, and a transparent PNG would
      // otherwise come out with a black background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        img,
        box.x * scaleX,
        box.y * scaleY,
        box.width * scaleX,
        box.height * scaleY,
        0,
        0,
        preset.width,
        preset.height
      );

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => (result ? resolve(result) : reject(new Error('Could not read the cropped image'))),
          'image/jpeg',
          0.92
        );
      });

      await onApply(blob, { width: preset.width, height: preset.height });
    } catch (err) {
      console.error('Crop failed:', err);
      setLoadError(
        err?.name === 'SecurityError'
          ? 'This image could not be read for cropping'
          : 'Could not apply the crop'
      );
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Crop image" size="large" className="image-cropper-modal">
      <div className="image-cropper">
        <div className="image-cropper-presets">
          {CROP_PRESETS.map((option, index) => (
            <button
              key={option.label}
              type="button"
              className={`image-cropper-preset${index === presetIndex ? ' selected' : ''}`}
              onClick={() => setPresetIndex(index)}
              disabled={loading || applying}
            >
              {option.label}
            </button>
          ))}
          <span className="image-cropper-output">
            Output {preset.width}×{preset.height}
          </span>
        </div>

        <div className="image-cropper-stage">
          {loading && (
            <div className="image-cropper-status">
              <Loader2 size={20} className="animate-spin" />
              <span>Loading image…</span>
            </div>
          )}

          {!loading && loadError && <div className="image-cropper-status error">{loadError}</div>}

          {!loading && objectUrl && (
            <div className="image-cropper-canvas-wrap">
              <img
                ref={imageRef}
                src={objectUrl}
                alt="Crop source"
                className="image-cropper-image"
                onLoad={resetBox}
                draggable={false}
              />

              {box && (
                <div
                  className="image-cropper-box"
                  style={{ left: box.x, top: box.y, width: box.width, height: box.height }}
                  onPointerDown={onPointerDown('move')}
                  onPointerMove={onPointerMove}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                >
                  {['nw', 'ne', 'sw', 'se'].map((corner) => (
                    <span
                      key={corner}
                      className={`image-cropper-handle image-cropper-handle--${corner}`}
                      onPointerDown={onPointerDown(corner)}
                      onPointerMove={onPointerMove}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="image-cropper-footer">
          <button type="button" className="image-cropper-btn" onClick={onCancel} disabled={applying}>
            Cancel
          </button>
          <button
            type="button"
            className="image-cropper-btn image-cropper-btn--primary"
            onClick={handleApply}
            disabled={!box || loading || applying || !!loadError}
          >
            {applying ? 'Applying…' : 'Apply crop'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImageCropper;
